/**
 * Expandable sections.
 */
class Expandable {
	/**
	 * Add event handlers to expand buttons.
	 *
	 * @param { Element } container A DOM element that contains the buttons to apply behaviour to.
	 */
	constructor( container ) {
		this.buttons = container.querySelectorAll( 'button[aria-expanded][aria-controls]' );

		this.buttons.forEach( button => {
			const expandable = Expandable.getExpandable( button );
			if ( ! expandable ) {
				return;
			}
			const expanded = ( button.getAttribute( 'aria-expanded' ) === 'true' );
			expandable.hidden = ! expanded;
			button.addEventListener( 'click', this.expandableButtonClick );
		} );

		// Open first element in accordion group.
		Expandable.openFirstAccordion();
	}

	/**
	 * Expandable button click handler.
	 *
	 * Toggle aria-expanded attribute on button and expandable section hidden property when the button is clicked.
	 *
	 * @param {Event} event Click event on a button that expands a section.
	 */
	expandableButtonClick( event ) {
		event.preventDefault();

		const button = event.target;
		Expandable.toggleElement( button );

		// If we're in a group of accordions, close the other open ones.
		Expandable.toggleSiblings( button );
	}

	/**
	 * Find the section a button controls.
	 *
	 * Looks inside the button's own expandable element first: a block duplicated
	 * in the editor can repeat the ID, and document.getElementById() would return
	 * the first section with it.
	 *
	 * @param {Element} button The button.
	 * @returns {Element|null} The section, or null if there is none.
	 */
	static getExpandable( button ) {
		const id = button.getAttribute( 'aria-controls' );
		const container = button.closest( '.kaplan-expandable' );
		const ownSection = container ? container.querySelector( `#${ CSS.escape( id ) }` ) : null;

		return ownSection ?? document.getElementById( id );
	}

	/**
	 * Toggle button/expandable element.
	 *
	 * @param {Element} button The current button.
	 */
	static toggleElement( button ) {
		const expandable = Expandable.getExpandable( button );
		if ( ! expandable ) {
			return;
		}

		const expanded = ( button.getAttribute( 'aria-expanded' ) === 'true' );
		expandable.hidden = expanded;
		expandable.closest( '.kaplan-expandable' ).setAttribute( 'data-open', ! expanded );
		button.setAttribute( 'aria-expanded', ! expanded );

		if ( button.hasAttribute( 'data-opened-label' ) ) {
			const toReplace = button.innerHTML;
			button.innerHTML = button.getAttribute( 'data-opened-label' );
			button.setAttribute( 'data-opened-label', toReplace );
		}
	}

	/**
	 * Toggle sibling expandable elements.
	 *
	 * @param {Element} button The current button.
	 */
	static toggleSiblings( button ) {
		const expanded = ( button.getAttribute( 'aria-expanded' ) === 'true' );
		const expandableGroup = button.closest( '.kaplan-accordion-group' );
		if ( ! expanded || ! expandableGroup ) {
			return;
		}

		const expandableGroupSiblingButtons =
			Array.from( expandableGroup.querySelectorAll( 'button[aria-expanded][aria-controls]' ) )
				.filter( element => element !== button );

		expandableGroupSiblingButtons.forEach( button => {
			const expanded = ( button.getAttribute( 'aria-expanded' ) === 'true' );
			if ( ! expanded ) {
				return;
			}
			Expandable.toggleElement( button );
		} );

		// Scroll the page to the expandable element.
		if ( ! Expandable.isInViewport( button ) ) {
			button.scrollIntoView( {
				behavior: 'smooth',
			} );
		}
	}

	/**
	 * Open first element in accordion group.
	 */
	static openFirstAccordion() {
		const expandableGroups = document.querySelectorAll( '.kaplan-accordion-group' );
		if ( ! expandableGroups ) {
			return;
		}

		expandableGroups.forEach( group => {
			const firstButton = group.querySelector( 'button[aria-expanded][aria-controls]' );
			const closed = ( firstButton?.getAttribute( 'aria-expanded' ) === 'false' );
			if ( ! closed ) {
				return;
			}
			Expandable.toggleElement( firstButton );
		} );
	}

	/**
	 * Removes event listeners.
	 */
	unmount() {
		this.buttons.forEach( button => {
			button.removeEventListener( 'click', this.expandableButtonClick );
		} );
	}

	/**
	 * Check if an element is in the viewport.
	 *
	 * @param {Element} element The element to check.
	 * @returns {boolean} True if the element is in the viewport, false otherwise.
	 */
	static isInViewport( element ) {
		const rect = element.getBoundingClientRect();
		return (
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom <= ( window.innerHeight || document.documentElement.clientHeight ) &&
			rect.right <= ( window.innerWidth || document.documentElement.clientWidth )
		);
	}
}

export default Expandable;
